import { DestroyRef, Directive, ElementRef, effect, inject, input } from '@angular/core';

/**
 * Selector list for "things a user can Tab to". [tabindex="-1"] is excluded on
 * purpose: the dialog panel itself carries tabindex="-1" so it can be focused
 * programmatically without ever becoming a Tab stop.
 */
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
  'textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

function isVisible(element: HTMLElement): boolean {
  // offsetParent is null for display:none subtrees. In jsdom every element
  // reports null, so the hidden-attribute check carries the specs.
  if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') {
    return false;
  }
  return true;
}

/**
 * Hand-rolled focus trap - no CDK in this project.
 *
 * Bound to isTop so it is live on the TOP dialog only: with three dialogs open
 * the two underneath must not fight the top one for focus. The previously
 * focused element is captured when the trap is created and restored when it is
 * destroyed, so Esc on a dialog opened from a row action returns the caret to
 * that row's button.
 */
@Directive({
  selector: '[appTabTrap]',
  host: {
    '(keydown)': 'onKeydown($event)',
    '(document:focusin)': 'onDocumentFocusIn($event)',
  },
})
export class TabTrapDirective {
  /** Active only on the top-most dialog. */
  readonly isTop = input<boolean>(true);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly previouslyFocused: HTMLElement | null = activeElement();
  private moved = false;

  constructor() {
    // Runs on creation and again whenever this instance becomes the top one
    // because the dialog above it closed.
    effect(() => {
      if (this.isTop()) queueMicrotask(() => this.focusFirst());
    });

    inject(DestroyRef).onDestroy(() => {
      // Only restore if this trap actually took focus; otherwise a background
      // dialog closing would yank focus off the dialog still on screen.
      if (this.moved) this.previouslyFocused?.focus?.();
    });
  }

  focusFirst(): void {
    if (!this.isTop()) return;
    const target = this.focusable()[0] ?? this.host.nativeElement;
    target.focus();
    this.moved = true;
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.isTop()) return;
    const stops = this.focusable();
    if (!stops.length) {
      event.preventDefault();
      this.host.nativeElement.focus();
      return;
    }
    const first = stops[0];
    const last = stops[stops.length - 1];
    const current = activeElement();

    if (event.shiftKey && (current === first || current === this.host.nativeElement)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * The escape hatch a keydown handler cannot cover: a click, a browser
   * find-bar jump or a programmatic focus() landing outside the panel. Only
   * the top trap pulls focus back, so the stack never oscillates.
   */
  protected onDocumentFocusIn(event: FocusEvent): void {
    if (!this.isTop() || !this.moved) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (this.host.nativeElement.contains(target)) return;
    this.focusFirst();
  }

  private focusable(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      isVisible
    );
  }
}

function activeElement(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement ? active : null;
}
